import React, { useEffect, useContext } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router";
import { GlobalStateContext } from "../../controller/GlobalState";
import { blogPosts } from "../../controller/global";
import ExploreOtherCaseStudy from "../../component/ExploreOtherCaseStudy";

// What InQmise built on this engagement — both figures come from the solution
// section below. The client's own history (Pre-Fill Services: founded 2002, a
// suite of 20+ products, ~500 MGA offices) is kept separate and labelled as
// theirs: mixed into one panel it read as InQmise's track record, and AI
// answer engines lift panels like this out of context.
const QUICK_FACTS = [
  { value: "40+", label: "Carrier integrations we built" },
  { value: "8 sec", label: "Premium indications, all carriers" },
];

const CLIENT_FACTS = [
  { value: "Since 2002", label: "Pre-Fill Services in business" },
  { value: "Nearly 500", label: "MGA offices using their suite" },
];

const ECOSYSTEM = [
  {
    title: "EZRater",
    desc: "Comparative rater and underwriting dashboard for GL and Property. Delivers premium indications from 40+ carriers in 8 seconds, produces a client proposal in under 4 minutes, and surfaces underwriting notes directly from carriers. A retailer-facing web portal extends quoting access in mobile, tablet, and desktop formats.",
  },
  {
    title: "Pre-Fill Data Collector",
    desc: "Merges risk and applicant data from 8 distinct sources into a single clean record, eliminating redundant re-entry across systems.",
  },
  {
    title: "Pre-Fill Publishing Engine",
    desc: "Generates the finalized policy document as a production-ready PDF, removing manual document assembly from the issuance workflow.",
  },
  {
    title: "EZ Forms",
    desc: "Provides underwriters with the precise required forms for every carrier, ensuring compliance without manual lookup.",
  },
  {
    title: "Policy Exporter",
    desc: "Bridges policy data to carriers using Documaker Desktop and to major carrier portals including PennAmerica, Atlantic Casualty, Nautilus, and Western World.",
  },
  {
    title: "EZ PL (Personal Lines)",
    desc: "A full Quote/Bind/Issue product for personal lines featuring retailer access, automatic quote letters, policy issuance, and self-administered rate and rule changes.",
  },
  {
    title: "PDFReader",
    desc: "In production since 2003, this tool extracts structured data from ACORD applications and spreadsheets — including scanned, faxed, and handwritten submissions — applies over 11,000 data quality repairs, and routes the cleaned data to the target management system.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the facts panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag className={className} custom={i} variants={fadeUp} initial={reduceMotion ? false : "hidden"} whileInView="show" viewport={{ once: true, amount: 0.2 }} {...rest}>
      {children}
    </MotionTag>
  );
}

const EZRater = () => {
  const caseStudy = blogPosts.find((post) => post.slug === "ezrater");
  const { setIsOpen } = useContext(GlobalStateContext);
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    // pageDescriptions.js holds the title this route ships with; useSocialMeta
    // overrides from there. Kept in step rather than removed — see Technologies.jsx.
    document.title = "EZRater: 40+ Insurance Carrier Quotes in 8 Seconds | InQmise";
    const description = document.createElement("meta");
    description.name = "description";
    description.content = "How we built EZRater: one submission, premium indications from 40+ surplus lines carriers in 8 seconds, by mapping every carrier's own dialect of ACORD XML.";
    document.head.appendChild(description);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/case-studies/ezrater");
  }, []);

  const openContactModal = () => setIsOpen(true);
  const goToCaseStudies = (e) => { e.preventDefault(); navigate("/case-studies"); };

  return (
    <main className="editorial-ai pt-20">
      {/* HERO */}
      <header
        style={caseStudy?.image ? {
          backgroundImage: `linear-gradient(rgba(251,252,248,.5),rgba(251,252,248,.8)), url(${caseStudy.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        } : undefined}
      >
        <div className="ea-hero ea-wrap">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <a className="ea-text-link" style={{ display: "inline-block", marginBottom: 14 }} href="/case-studies" onClick={goToCaseStudies}>← All Case Studies</a>
            <p className="ea-eyebrow">Case study · {caseStudy?.category}</p>
            <h1>EZRater: insurance quote comparison software that cracked cross-carrier quoting</h1>
            <p className="ea-lede">
              How we built a comparative rating platform for the surplus lines industry — solving the ACORD interoperability problem that made real-time multi-carrier quoting seem impossible.
            </p>
            <div className="ea-actions">
              <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your project">Start your project ↗</button>
              <a className="ea-text-link" href="/case-studies" onClick={goToCaseStudies}>Explore other case studies</a>
            </div>
          </Reveal>

          <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
            <div className="ea-engine-top"><span /> EZRATER / WHAT WE BUILT <b>LIVE</b></div>
            {QUICK_FACTS.map((fact) => (
              <div className="cd-facts-row" key={fact.label}>
                <strong>{fact.value}</strong>
                <span>{fact.label}</span>
              </div>
            ))}
            <div className="ea-engine-top" style={{ marginTop: 18 }}><span /> THE CLIENT / PRE-FILL SERVICES</div>
            {CLIENT_FACTS.map((fact) => (
              <div className="cd-facts-row" key={fact.label}>
                <strong>{fact.value}</strong>
                <span>{fact.label}</span>
              </div>
            ))}
          </Reveal>
        </div>
      </header>

      {/* CLIENT BACKGROUND */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Client background</p>
          <h2 className="cd-h2">Client Background</h2>
          <div className="std-overview">
            <p>
              Pre-Fill Services is an Atlanta-based insurtech software company with a singular mission:
              eliminate unnecessary data entry from the surplus lines insurance workflow. Founded by a 40-
              year insurance industry veteran with deep experience across retail agencies, MGAs, and
              carriers, the company has spent over two decades building electronic "bridges" that connect the
              fragmented systems and participants of the surplus lines world. Since 2002, their suite of more
              than 20 products has been adopted by nearly 500 MGA offices across all 50 states —
              collectively saving billions of keystrokes for underwriters, brokers, and administrators.
            </p>
          </div>
        </Reveal>
      </section>

      {/* THE MARKET */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">The market</p>
          <h2 className="cd-h2">What Surplus Lines Is, and Why It Fragments</h2>
          <div className="std-overview">
            <p>
              Most insurance is written in the admitted market: carriers licensed by a state, using
              rates and forms filed with and approved by that state&rsquo;s regulator. Standardized product,
              standardized paperwork, and a state guaranty fund behind it.
            </p>
            <p>
              Surplus lines &mdash; also called excess and surplus, or E&amp;S &mdash; is where the risks go that
              the admitted market will not write. A contractor with a poor loss history, a property in a
              wildfire zone, an unusual liability exposure with no filed rate to price it against.
              Non-admitted carriers can write these because they are exempt from filing rates and forms,
              and that exemption is precisely what creates the problem this platform exists to solve:
              <strong> with no filed standard, every carrier prices and structures its own way.</strong>
            </p>
            <p>
              Two carriers looking at the same contractor will want different information, apply
              different eligibility rules, and have different appetites that shift with their book. One
              may decline the risk outright while another quotes it keenly. There is no published rate
              to compare against, so the only way to know what a risk costs is to ask every carrier that
              might want it.
            </p>
            <p>
              That is why comparative rating matters far more here than in admitted lines, and why it is
              far harder. The Managing General Agents in the middle &mdash; who hold binding authority on
              behalf of carriers and place the business &mdash; carry the cost of that fragmentation
              directly, in staff hours spent re-keying the same risk into portal after portal.
            </p>
          </div>
        </Reveal>
      </section>

      {/* THE CHALLENGE */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">The challenge</p>
            <h2 className="cd-h2">The Core Challenge: Quote Aggregation Across a Fragmented Carrier Ecosystem</h2>
            <div className="std-overview">
              <p>
                At the heart of the engagement was a deceptively complex problem: how do you build a
                comparative rating platform — a "Kayak for insurance" — when every carrier speaks a different
                data language?
              </p>
              <p>
                When a broker or underwriter needed premium indications from multiple carriers, the traditional
                process meant logging into each carrier portal individually, re-keying the same risk data
                repeatedly, and manually collating results. For a busy MGA office handling dozens of
                submissions a day, this was an enormous productivity drain and a significant source of human
                error.
              </p>
              <p>
                The vision for EZRater was straightforward: a user enters just three data elements, and within 8
                seconds receives premium indications from every linked carrier simultaneously. But executing
                that vision required solving a deep technical challenge — getting heterogeneous carrier systems
                to accept, process, and respond to a single unified data submission.
              </p>
              <p>
                The industry standard for insurance data exchange is ACORD (Association for Cooperative
                Operations Research and Development) — a structured XML-based schema that defines how
                insurance data should be formatted and transmitted. The problem was that while carriers
                nominally supported ACORD, each one implemented it differently: varying field mappings, non-
                standard extensions, different versions of the schema, and entirely divergent API architectures
                ranging from modern REST endpoints to legacy SOAP services and flat-file uploads. Building a
                true aggregator meant not just sending ACORD data, but dynamically transforming it into
                whatever dialect each individual carrier actually expected — reliably, at speed, and at scale.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OUR SOLUTION */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Our solution</p>
          <h2 className="cd-h2">Our Solution: EZRater — The Comparative Rater</h2>
          <div className="std-overview">
            <p>
              EZRater was the flagship product of this engagement — a comparative rating and underwriting
              dashboard for GL and Property that we built from the ground up. The user experience was
              deliberately stripped back to its essentials: enter three data elements, receive premium
              indications from every connected carrier in 8 seconds. Behind that deceptively simple interface,
              the platform was doing sophisticated heavy lifting.
            </p>
            <p>
              The core technical challenge was cross-carrier data compatibility. Each carrier had its own
              interpretation of the ACORD standard — different schema versions, non-standard field
              extensions, and API architectures ranging from modern REST to legacy SOAP. We engineered
              a carrier adapter framework in which each of the 40+ integrated carriers had its own
              configurable mapping profile, translating the user's submission into the exact format that carrier
              required before dispatch. This meant new carrier integrations could be built and deployed
              independently, without touching core platform code — a major accelerator as the network of
              connected carriers grew.
            </p>
            <p>
              Beyond raw quoting, EZRater functioned as a full underwriting dashboard: surfacing
              underwriting notes directly from carriers alongside premium results, and generating a polished
              client proposal document in under 4 minutes. A companion retailer portal extended the same
              quoting capability to the client's own website in mobile, tablet, and desktop formats — giving
              retail brokers direct access without requiring MGA staff involvement for every enquiry.
            </p>
          </div>
        </Reveal>
      </section>

      {/* THE LATENCY PROBLEM */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">The hard constraint</p>
            <h2 className="cd-h2">Eight Seconds, Across Forty Carriers You Don&rsquo;t Control</h2>
            <div className="std-overview">
              <p>
                The eight-second figure is the whole product. Beyond roughly ten seconds a user stops
                waiting and opens a carrier portal instead, which is the behavior the platform exists to
                replace. But the platform controls none of the systems it depends on.
              </p>
              <p>
                <strong>Sequential dispatch is arithmetically impossible.</strong> Forty carriers averaging
                even half a second each is twenty seconds. Submissions have to leave in parallel, which
                means the slowest carrier &mdash; not the average one &mdash; sets the user&rsquo;s experience.
              </p>
              <p>
                <strong>So the deadline has to belong to the platform, not the carriers.</strong> A legacy
                SOAP endpoint having a bad afternoon cannot be allowed to hold a broker&rsquo;s screen hostage.
                That forces an uncomfortable product decision: return thirty-seven quotes at eight
                seconds, or make everyone wait for the last three. Partial results within the deadline
                are far more useful than complete results outside it, provided the interface is honest
                about which carriers have not answered yet rather than quietly presenting an incomplete
                comparison as a complete one.
              </p>
              <p>
                <strong>Failure has to be distinguishable from decline.</strong> &ldquo;This carrier did not
                respond&rdquo; and &ldquo;this carrier will not write this risk&rdquo; look identical in a results list and
                mean entirely different things to an underwriter. One is worth retrying; the other is
                information about the risk itself.
              </p>
              <p>
                This is also why the adapter framework and the eight-second target are the same problem
                rather than two. Each of the 40+ carriers has its own mapping profile precisely so that
                translation is configuration applied at dispatch rather than logic executed per request
                &mdash; and so that a slow or failing carrier is one isolated adapter, not a fault in a
                shared path that degrades every quote in flight.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY ADAPTERS */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">The architectural decision</p>
          <h2 className="cd-h2">Why Per-Carrier Adapters Beat One Canonical Schema</h2>
          <div className="std-overview">
            <p>
              The instinctive design for an aggregator is a canonical model: define one internal
              representation of a risk, translate every carrier into and out of it, and keep the
              complexity in one place. It is the approach most teams reach for, and in a market like
              this it fails predictably.
            </p>
            <p>
              A canonical schema has to be the union of everything every carrier needs. Each new carrier
              with a non-standard extension widens it, and every widening touches shared code that all
              the existing carriers already depend on. Onboarding carrier forty-one becomes a change
              that risks carriers one through forty &mdash; so integrations slow down exactly as the
              network becomes valuable, and the incentive to add carriers works against the effort
              required to add them.
            </p>
            <p>
              Per-carrier mapping profiles invert that. The cost of a new integration stays roughly
              constant no matter how many already exist, because a carrier&rsquo;s peculiarities live in its
              own profile rather than in shared structure. A carrier changing its API version is a
              configuration change confined to one file. The trade-off is real &mdash; forty profiles is
              genuinely more total configuration than one schema, and there is no single place to read
              the whole system&rsquo;s data model. For a platform whose value grows directly with carrier
              count, that is the right trade to make.
            </p>
          </div>
        </Reveal>
      </section>

      {/* SUPPORTING PLATFORM */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">Supporting platform</p>
            <h2 className="cd-h2">Supporting Platform: STPS as the Back-Office Quote Management Layer</h2>
            <div className="std-overview">
              <p>
                Running behind EZRater was the Straight Through Processing System (STPS) — a back-office
                management platform purpose-built to track, audit, and manage the full lifecycle of every quote
                sent and received through the aggregator. Where EZRater was the front-stage experience for
                underwriters and brokers, STPS was the operational backbone: logging every outbound
                ACORD submission, capturing carrier responses, flagging failures, and providing administrators
                with a complete audit trail of quoting activity across the office.
              </p>
              <p>
                STPS gave MGA back-office teams visibility they had never had before — a single dashboard
                showing every quote in flight, every response received, and every submission that required
                attention. It also handled the enrichment pipeline: pulling pre-fill data from external sources,
                applying over 11,000 data quality repair rules, and ensuring that every submission reaching a
                carrier was clean, complete, and correctly structured before it left the platform.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRODUCT ECOSYSTEM */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Product ecosystem</p>
          <h2 className="cd-h2">The Wider Product Ecosystem</h2>
          <div className="std-overview">
            <p>
              EZRater and STPS sit at the center of a broader suite of over 20 interconnected products, each
              addressing a specific friction point across the MGA workflow:
            </p>
          </div>
        </Reveal>
        <div className="re-numbered-list">
          {ECOSYSTEM.map((item, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="re-numbered-row" key={item.title}>
              <div className="re-numbered-row-num">{String(i + 1).padStart(2, "0")}</div>
              <div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OUTCOMES & IMPACT */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">Outcomes & impact</p>
            <h2 className="cd-h2">Outcomes & Impact</h2>
            <div className="std-overview">
              <p>
                EZRater fundamentally changed what was possible for surplus lines MGA offices. What
                previously required multiple portal logins, manual re-keying, and 20–30 minutes of effort per
                submission was reduced to a three-field form and an 8-second response. The cross-carrier
                ACORD adapter framework — the hardest engineering problem in the project — became a
                durable competitive moat: as new carriers were onboarded, each integration compounded the
                platform's value without proportional engineering cost. Behind it all, STPS gave back-office
                teams the operational visibility and audit control they needed to manage high submission
                volumes with confidence.
              </p>
              <p>
                Today, Pre-Fill Services' products are embedded in nearly 500 MGA offices across every US
                state. The PDFReader alone, in production for over 20 years, has processed countless ACORD
                submissions backed by a data quality repair engine that continues to grow. The broader suite
                represents what becomes possible when 40 years of insurance domain expertise is paired with
                engineering that attacks the hardest interoperability problems at the root.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EXPLORE OTHER CASE STUDIES */}
      <ExploreOtherCaseStudy currentSlug="ezrater" />

      {/* CTA */}
      <section className="ea-cta" style={{ textAlign: "center" }}>
        <div className="ea-wrap">
          <Reveal as="div" reduceMotion={reduceMotion} style={{ maxWidth: 560, margin: "0 auto" }}>
            <div className="ea-eyebrow" style={{ justifyContent: "center" }}>Ready when you are</div>
            <h2 style={{ margin: "16px 0" }}>Ready to Transform Your Business?</h2>
            <p style={{ margin: "0 auto 28px", maxWidth: 460 }}>
              Let's discuss how we can build sophisticated solutions for your most complex problems.
            </p>
            <button className="ea-button ea-primary" onClick={openContactModal}>Get in Touch</button>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default EZRater;
